import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { api, setAccessToken } from './api/client.js';
import type { SessionUser } from './api/types.js';
import { LoginScreen } from './components/LoginScreen.js';
import { CustomerApp } from './customer/CustomerApp.js';
import { AdminShell } from './admin/AdminShell.js';
import { ChangePasswordForm } from './components/ChangePasswordForm.js';
import starLogo from '@bqs/templates/assets/brand/STAR_Horizontal_Light.svg';

/**
 * App-Huelle: Anmeldung, Rollenweiche, Kopfzeile.
 *
 * Zwei Bereiche, strukturell getrennt:
 *
 * ```text
 * Kundenbereich   account_user, account_admin   Katalog, Suche, Angebot, Historie
 * Adminbereich    sales, operator_admin         Kunden, Konditionen, Preislisten, ...
 * ```
 *
 * Die Weiche entscheidet nur, was **gezeigt** wird. Was **erlaubt** ist, entscheidet
 * der Server -- jede Admin-Route ist dort mit @Roles gesichert.
 */
export function App(): React.JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);

  // Sitzung ueber das Refresh-Cookie wiederherstellen, damit ein Neuladen im
  // Adminbereich nicht abmeldet. Das Access-Token bleibt im Speicher.
  const [restoring, setRestoring] = useState(true);
  useEffect(() => {
    api
      .post<{ accessToken: string; user: SessionUser }>('/api/auth/refresh')
      .then((r) => {
        setAccessToken(r.accessToken);
        setUser(r.user);
      })
      .catch(() => undefined)
      .finally(() => setRestoring(false));
  }, []);

  async function logout(): Promise<void> {
    await api.post('/api/auth/logout').catch(() => undefined);
    setAccessToken(null);
    setUser(null);
  }

  if (restoring) return <div className="shell narrow" />;

  if (user === null) {
    return <LoginScreen onLogin={setUser} />;
  }

  // Passwortwechsel erzwingen, bevor irgendetwas anderes moeglich ist.
  if (user.mustChangePassword) {
    return (
      <>
        <Header user={user} onLogout={() => void logout()} />
        <main className="shell narrow">
          <ChangePasswordForm
            onDone={() => setUser({ ...user, mustChangePassword: false })}
          />
        </main>
      </>
    );
  }

  const intern = user.role === 'sales' || user.role === 'operator_admin';

  return (
    <BrowserRouter>
      <Header user={user} onLogout={() => void logout()} intern={intern} />
      <Routes>
        {intern ? (
          <>
            <Route path="/admin/*" element={<AdminShell user={user} />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </>
        ) : (
          <>
            <Route path="/" element={<CustomerApp />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </BrowserRouter>
  );
}

function Header({
  user,
  onLogout,
  intern = false,
}: {
  user?: SessionUser;
  onLogout?: () => void;
  intern?: boolean;
}): React.JSX.Element {
  return (
    <header className={intern ? 'topbar topbar-admin' : 'topbar'}>
      <div className="brand">
        {/*
          Finales STAR-Logo aus dem freigegebenen Markenpaket, unveraendert.
          Light-Variante (Navy-Schrift, Orange-Marke) auf einer hellen Flaeche:
          Weder die Light- noch die Dark-Variante des Logos ist fuer die tatsaechliche
          Kopfzeilenfarbe (K+Z-Blau #0075bc) vorgesehen -- das Markenpaket zielt auf
          "hell" bzw. auf "Ink Navy #0e1b26". Nachgemessen (WCAG-Kontrast) versagen
          beide direkt auf #0075bc: Orange-Marke 1.3:1 (Dark), Navy-Schrift 2.2:1
          (Light) -- beide weit unter den fuer Grafik/Text geforderten 3:1 bzw. 4.5:1.
          Deshalb bekommt die Light-Variante hier eine kleine helle Flaeche (Chip)
          als Untergrund, nicht der Kopfzeilenhintergrund selbst und nicht das Logo.
          Damit: Navy-Schrift 10.9:1, Orange-Marke 3.8:1 -- beide bestehen.
        */}
        <span className="brand-logo-chip">
          <img
            className="brand-logo"
            src={starLogo}
            alt="STAR – Sales Tool for Automated Requests"
          />
        </span>
        {intern && <span className="brand-scope">Administration</span>}
      </div>
      {user !== undefined && (
        <div className="who">
          <span>
            {user.displayName}
            {user.accountName !== null && ` · ${user.accountName}`}
            {intern && ` · ${user.role === 'operator_admin' ? 'Betreiber' : 'Vertrieb'}`}
          </span>
          <button type="button" className="link" onClick={onLogout}>
            Abmelden
          </button>
        </div>
      )}
    </header>
  );
}
